import { useUpdateMultipleSlidesMutation } from "@api/slides-library";
import Button from "@components/ui/button";
import { cn } from "@lib/cn";
import { FoldersSelect } from "@modules/slides-library/features/folders";
import {
  DialogBase,
  useSlidesLibraryStore,
} from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Spinner from "@/components/ui/spinner";
import { handleAsyncOperation } from "@/shared";
import { useGetSlidesFoldersQuery } from "@/shared/services";
import type { TSavedSlide, TSlidesFolder } from "@/shared/types";
import { SPECIAL_FOLDERS } from "@modules/slides-library/features/folders/constants";
import { useGetSavedSlides } from "../hooks";

const DIALOG_ID = "move-many-slides-dialog";

const SlideMoveManyDialog = ({ ids }: { ids: string[] }) => {
  "use no memo";
  const dispatch = useAppDispatch();
  const { data: folders } = useGetSlidesFoldersQuery();
  const [updateManySlides, { isLoading }] = useUpdateMultipleSlidesMutation();
  const { data } = useGetSavedSlides();
  const resetMultiSelectSlides = useSlidesLibraryStore(
    (s) => s.resetMultiSelectSlides,
  );

  const [folder, setFolder] = useState<{ title: string; id: string }>({
    title: "",
    id: "",
  });

  const slides = useMemo(() => {
    return data?.filter((slide: TSavedSlide) => ids.includes(slide._id));
  }, [data, ids]);

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        await updateManySlides({
          ids,
          folderId: folder.id,
        }).unwrap();
        resetMultiSelectSlides();
        toast.success(
          `${slides?.length} ${slides?.length > 1 ? "slides" : "slide"} moved to a folder "${folder.title}"`,
        );
        handleClose();
      },
      onError: () => toast.error("Failed to move slides"),
    });
  }, [
    updateManySlides,
    handleClose,
    isLoading,
    resetMultiSelectSlides,
    slides,
    ids,
    folder,
  ]);

  useEffect(() => {
    if (!folders) return;

    const slide = slides?.[0];
    if (!slide) return;
    const slideFolderId = slide.folderId ?? "";

    if (SPECIAL_FOLDERS[slideFolderId]) {
      setFolder({ title: SPECIAL_FOLDERS[slideFolderId], id: slideFolderId });
      return;
    }

    const folder = folders.find(
      (folder: TSlidesFolder) => folder._id === slideFolderId,
    );
    if (!folder) return;
    setFolder({ title: folder.title, id: folder._id });
  }, [folders, slides]);

  return (
    <DialogBase>
      <DialogBase.Header title="Move slides" onClose={handleClose} />
      <DialogBase.Content>
        <FoldersSelect folder={folder} setFolder={setFolder} />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!folder.id}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlideMoveManyDialog.id = DIALOG_ID;
export { SlideMoveManyDialog };
